<template>
  <div class="content">
    <a-scroll-table class="table"
                    :columns="DSS_ORDER_COLUMNS"
                    :data-source="dataSource"
                    :pagination="false">
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'theme_title'">
          <span>
            {{ record.theme.title }}
          </span>
        </template>
        <template v-if="column.dataIndex === 'status'">
          <span>
            {{ record.status === 0 ? $t('DssOrder.DesginShareOrder.unpaid') : record.status === 1 ?
              $t('DssOrder.DesginShareOrder.alreadyPaid') : record.status === 2 ?
                $t('DssOrder.DesginShareOrder.lockedOrder') : $t('DssOrder.DesginShareOrder.refunded') }}
          </span>
        </template>
        <template v-if="column.dataIndex === 'pay_type'">
          <span>
            {{ record.pay_type === 0 ? $t('DssOrder.DesginShareOrder.alipay') : record.pay_type === 1 ?
              $t('DssOrder.DesginShareOrder.wxpay') : record.pay_type === 2 ? $t('DssOrder.DesginShareOrder.wxMini') :
                $t('DssOrder.DesginShareOrder.walletPay') }}
          </span>
        </template>
        <template v-if="column.dataIndex === 'operation'">
          <a-tooltip placement="bottom"
                     :color="'#111'">
            <template #title>
              <span>{{ $t('DssOrder.DesginShareOrder.details') }}</span>
            </template>
            <file-search-outlined class="operation-icon"
                                  @click="openDssOrderView(record)" />
          </a-tooltip>
        </template>
      </template>
    </a-scroll-table>
    <div class="footer">
      <APagination v-model:current="pagination.current"
                   v-model:pageSize="pagination.pageSize"
                   :show-size-changer="true"
                   :total="pagination.total"
                   @change="handleTableChange" />
    </div>
    <a-modal v-model:visible="dssOrderViewVisit"
             width="900px"
             :body-style="{ height: '500px', overflow: 'auto' }"
             :title="currentDssOrderData?.theme.title"
             :destroy-on-close="true"
             @cancel="handleCancel">
      <div class="desgin-share-modal-desginer">{{ $t('DssOrder.DesginShareOrder.designer') }}：{{
        currentDssOrderData?.theme.user.username }}</div>
      <a-tag v-if="currentDssOrderData?.status != 1 && currentDssOrderData?.status != 0"
             color="orange">{{
              $t('DssOrder.DesginShareOrder.hintNoRefunds') }}</a-tag>
      <div class="desgin-share-modal-desc">
        <span>{{ $t('DssOrder.DesginShareOrder.refundInstructions') }}</span>
        <span>{{ $t('DssOrder.DesginShareOrder.refundConditions') }}</span>
      </div>
      <div class="desgin-share-modal-images">
        <div v-for="(item, index) in currentDssOrderData?.theme.shareImages"
             :key="index"
             class="desgin-share-modal-content">
          <div class="desgin-share-modal-image"><img :src="toUrl(toWatermark(item.image))"
                 alt=""></div>
          <div class="desgin-share-modal-text"><span class="desgin-share-modal-price">¥ {{ item.ref_price }}</span>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="desgin-share-modal-footer">
          <div class="desgin-share-modal-buy">

            <div>{{ $t('DssOrder.DesginShareOrder.designPackagePrice') }}:<span class="desgin-share-modal-price">¥ {{
              currentDssOrderData?.theme.price }}</span></div>
          </div>
          <div class="desgin-share-modal-footer-buttons">
            <div class="desgin-share-modal-footer-button">
              <a-button key="back"
                        @click="handleCancel">{{ $t('DssOrder.DesginShareOrder.dancel') }}</a-button>
              <a-popconfirm :title="$t('DssOrder.DesginShareOrder.confirmRefund')"
                            :ok-text="$t('DssOrder.DesginShareOrder.isRefund')"
                            :cancel-text="$t('DssOrder.DesginShareOrder.dancel')"
                            @confirm="onRequestRefund">
                <a-button key="submit"
                          type="primary"
                          :loading="confirmLoading"
                          :disabled="currentDssOrderData?.status != 1">{{ $t('DssOrder.DesginShareOrder.requestRefund')
                          }}</a-button>
              </a-popconfirm>
            </div>
          </div>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AScrollTable, APagination } from '@iota-cn/ui-components'
import dayjs from 'dayjs'
import { computed, onMounted, ref } from 'vue'
import { fetchDssOrderList, refundWxpay, refundWxpayQuery, refundAlipay, refundAlipayQuery, refundWalletPay } from "../api"
import { DssOrder } from "../configure"
import { toUrl, toWatermark, hideMobileNumber } from '@/utils'
import { message } from 'ant-design-vue'
import type { TableColumnsType } from 'ant-design-vue'
import { useI18n } from "vue-i18n"

const { t } = useI18n()
const dataSource = ref<DssOrder[]>([])
const currentDssOrderData = ref<DssOrder>()
const dssOrderViewVisit = ref<boolean>(false)
const confirmLoading = ref<boolean>(false)
const pagination = ref({
  current: 1,
  total: 0,
  offset: 0,
  pageSize: 20,
  showSizeChanger: true,
  pageSizeOptions: ['20', '40', '60', '80'], // 每页中显示的数据
  showTotal: (total: number) => `${t('DssOrder.DesginShareOrder.total')}${total} 条`, // 分页中显示总的数据
  size: 'small',
})
const DSS_ORDER_COLUMNS = computed<TableColumnsType>(() => [
  {
    title: t('DssOrder.configure.theme_title'),
    dataIndex: 'theme_title',
    width: '25%',
    ellipsis: true,
  },
  {
    title: '下单时间',
    dataIndex: 'createdAt',
    width: '20%',
    ellipsis: true,
  },
  {
    title: '付款时间',
    dataIndex: 'pay_at',
    width: '20%',
    ellipsis: true,
  },
  {
    title: '价格',
    dataIndex: 'price',
    width: '10%',
    ellipsis: true,
  },
  {
    title: '订单状态',
    dataIndex: 'status',
    width: '15%',
    ellipsis: true,
  },
  {
    title: '支付方式',
    dataIndex: 'pay_type',
    width: '15%',
    ellipsis: true,
  }, {
    title: '操作',
    dataIndex: 'operation',
    width: '15%',
    ellipsis: true,
  }
])

onMounted(() => {
  init()
})

const wxpayQuery = async () => {
  const data = {
    number: currentDssOrderData.value?.number,
    out_trade_number: currentDssOrderData.value?.out_trade_number
  }
  await refundWxpayQuery(data).then((res) => {
    message.success(`${currentDssOrderData.value?.theme.title}${t('DssOrder.DesginShareOrder.refundSuccessfully')}！`)
    getDssOrderList()
  })
}

const alipayQuery = async () => {
  const data = {
    number: currentDssOrderData.value?.number,
    out_trade_no: currentDssOrderData.value?.out_trade_number
  }
  await refundAlipayQuery(data).then((res) => {
    message.success(`${currentDssOrderData.value?.theme.title}${t('DssOrder.DesginShareOrder.refundSuccessfully')}！`)
    getDssOrderList()
  })
}

const init = async () => {
  const data = {
    limit: 20,
    offset: 0,
    type: 0
  }
  await fetchDssOrderList(data).then((res: any) => {
    convert(res)
    dataSource.value = res.dssOrder
  })
}

const getDssOrderList = async () => {
  const data = {
    limit: pagination.value.pageSize,
    offset: pagination.value.offset,
    type: 0
  }
  await fetchDssOrderList(data).then((res: any) => {
    convert(res)
    dataSource.value = res.dssOrder
  })
}

const onRequestRefund = async () => {
  const data = {
    outTradeNo: '',
    trade_no: currentDssOrderData.value?.out_trade_number,
    refund_amount: currentDssOrderData.value?.price, // 退款金额，必填
    pay_type: currentDssOrderData.value?.pay_type
  }
  switch (currentDssOrderData.value?.pay_type) {
    case 0:
      data.outTradeNo = currentDssOrderData.value.number
      break
    case 1:
      data.outTradeNo = currentDssOrderData.value.number + '-PC'
      break
    case 2:
      data.outTradeNo = currentDssOrderData.value.number + '-APP'
      break
    case 3:
      data.outTradeNo = currentDssOrderData.value.number
      break
  }
  if (currentDssOrderData.value?.pay_type == 0) {
    await refundAlipay(data).then(res => {
      alipayQuery()
    }).catch(() => {
      message.error(`${currentDssOrderData.value?.theme.title}${t('DssOrder.DesginShareOrder.refundFailed')}`)
    })
  } else if (currentDssOrderData.value?.pay_type == 1) {
    await refundWxpay(data).then(res => {
      wxpayQuery()
    }).catch(() => {
      message.error(`${currentDssOrderData.value?.theme.title}${t('DssOrder.DesginShareOrder.refundFailed')}`)
    })
  } else if (currentDssOrderData.value?.pay_type == 3) {
    await refundWalletPay(data).then(res => {
      message.success(`${currentDssOrderData.value?.theme.title}${t('DssOrder.DesginShareOrder.refundSuccessfully')}！`)
    }).catch(() => {
      message.error(`${currentDssOrderData.value?.theme.title}${t('DssOrder.DesginShareOrder.refundFailed')}`)
    })
  }
}

const convert = (res: any) => {
  res.dssOrder.forEach((r: any) => {
    r.createdAt = dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss')
    r.pay_at = dayjs(r.pay_at).format('YYYY-MM-DD HH:mm:ss')
  })
  pagination.value.total = res.total
}

const openDssOrderView = (value: DssOrder) => {
  currentDssOrderData.value = value
  dssOrderViewVisit.value = true
}

const handleCancel = () => {
  dssOrderViewVisit.value = false
  currentDssOrderData.value = undefined
}

const handleTableChange = (current: number, pageSize: number) => {
  pagination.value.current = current
  pagination.value.offset = (current - 1) * pageSize
  init()
}

</script>
<style lang="less" scoped>
.content {
  width: 100%;
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;

  .operation-icon {
    font-size: 20px;
    cursor: pointer;
    margin: 0 10px;
    color: #999
  }

  .operation-icon:hover {
    color: #111;
  }


  .table {
    flex: 1;
    margin-top: 20px;
    overflow: hidden;
  }

  .footer {
    display: flex;
    justify-content: end;
  }
}
</style>
